// ─────────────────────────────────────────────────────────────
// HistoryView — All Sprints (planning, queued, active, completed)
//
// Section 1: Planning — sprints with status="planning" — actions:
//             Queue Sprint (admin password), Open in Board.
// Section 2: Sprint Queue — active and queued sprints with
//             expandable card list, status-aware actions.
// Section 3: Completed Sprints — historical sprint snapshots
//             with expandable card list.
// ─────────────────────────────────────────────────────────────

import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import QueueOutlinedIcon from "@mui/icons-material/QueueOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import type { Sprint, SprintHistoryEntry, SprintWithItems, FeatureCard } from "../types";
import { sprintsApi, sprintHistoryApi } from "../api/client";
import { useApi } from "../hooks/useApi";
import { PriorityBadge, TagList } from "../components/CardBadges";

// ── Ordinal helper ────────────────────────────────────────────

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

// ── AdminPasswordDialog ───────────────────────────────────────

interface AdminPasswordDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmColor?: "success" | "warning" | "error";
  confirmIcon?: React.ReactNode;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

function AdminPasswordDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmColor = "warning",
  confirmIcon,
  onClose,
  onSubmit,
}: AdminPasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setErrorMessage(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!password.trim()) {
      setErrorMessage("Password is required");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onSubmit(password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      if (
        msg.includes("403") ||
        msg.toLowerCase().includes("forbidden") ||
        msg.toLowerCase().includes("incorrect") ||
        msg.toLowerCase().includes("invalid")
      ) {
        setErrorMessage("Incorrect password");
      } else {
        setErrorMessage(msg);
      }
      setSubmitting(false);
    }
  }, [password, onSubmit]);

  const colorSx =
    confirmColor === "success"
      ? {
          backgroundColor: "success.main",
          color: "#fff",
          "&:hover": { backgroundColor: "success.dark" },
          "&.Mui-disabled": {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
          },
        }
      : confirmColor === "warning"
      ? {
          backgroundColor: "warning.main",
          color: "#fff",
          "&:hover": { backgroundColor: "warning.dark" },
          "&.Mui-disabled": {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
          },
        }
      : {};

  const defaultIcon =
    confirmColor === "success" ? (
      <PlayArrowOutlinedIcon fontSize="small" />
    ) : (
      <RemoveCircleOutlineIcon fontSize="small" />
    );

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        <TextField
          inputRef={inputRef}
          label="Admin Password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errorMessage) setErrorMessage(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !submitting) void handleSubmit();
          }}
          fullWidth
          autoFocus
          disabled={submitting}
          error={Boolean(errorMessage)}
          helperText={errorMessage ?? " "}
          autoComplete="current-password"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || !password.trim()}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              (confirmIcon ?? defaultIcon)
            )
          }
          sx={colorSx}
        >
          {submitting ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main view ─────────────────────────────────────────────────

export interface HistoryViewProps {
  /**
   * Called when the user clicks "Open in Board" on a planning sprint.
   * The parent should switch to the Kanban view and pre-select this sprint.
   */
  onOpenInBoard?: (sprintId: number) => void;
}

export function HistoryView({ onOpenInBoard }: HistoryViewProps = {}) {
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Per-sprint busy id so we can dim the relevant row while the API call
  // is in flight. Greg's 2026-05-15 policy removed the admin-password
  // confirmation dialogs — actions fire immediately on click and the JWT
  // gates the request. We surface failures inline rather than via modal.
  const [busySprintId, setBusySprintId] = useState<number | null>(null);

  // Planning sprints — ordered by created_at asc (server default)
  const {
    data: planningSprints,
    loading: planningLoading,
    error: planningError,
    refetch: refetchPlanning,
  } = useApi<Sprint[]>(() => sprintsApi.list("planning"), []);

  // Queued sprints — ordered by confirmed_at asc
  const {
    data: queuedSprints,
    loading: queuedLoading,
    error: queuedError,
    refetch: refetchQueued,
  } = useApi<Sprint[]>(() => sprintsApi.list("queued"), []);

  // Active sprints — should be at most one, but we handle it as a list
  const {
    data: activeSprints,
    loading: activeLoading,
    error: activeError,
    refetch: refetchActive,
  } = useApi<Sprint[]>(() => sprintsApi.list("active"), []);

  // Completed sprints via sprint history
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
  } = useApi<SprintHistoryEntry[]>(() => sprintHistoryApi.list(), []);

  const refetchQueue = useCallback(() => {
    refetchQueued();
    refetchActive();
    refetchPlanning();
  }, [refetchQueued, refetchActive, refetchPlanning]);

  // Generic sprint-action runner — wraps API call with busy state +
  // inline error handling so the four button-click handlers below stay
  // declarative.
  const runSprintAction = useCallback(
    async (
      sprint: Sprint,
      action: () => Promise<unknown>,
      successMessage: string,
      failurePrefix: string,
    ) => {
      setBusySprintId(sprint.id);
      setActionError(null);
      try {
        await action();
        setSnackbarMessage(successMessage);
        refetchQueue();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        setActionError(`${failurePrefix}: ${msg}`);
      } finally {
        setBusySprintId(null);
      }
    },
    [refetchQueue],
  );

  const handleQueueSprint = useCallback(
    (sprint: Sprint) =>
      runSprintAction(
        sprint,
        () => sprintsApi.confirm(sprint.id),
        `"${sprint.name}" added to queue`,
        "Failed to queue sprint",
      ),
    [runSprintAction],
  );

  const handleUnqueue = useCallback(
    (sprint: Sprint) =>
      runSprintAction(
        sprint,
        () => sprintsApi.unqueue(sprint.id),
        `"${sprint.name}" moved back to planning`,
        "Failed to un-queue sprint",
      ),
    [runSprintAction],
  );

  const handleStart = useCallback(
    (sprint: Sprint) =>
      runSprintAction(
        sprint,
        () => sprintsApi.start(sprint.id),
        `"${sprint.name}" started — Paul is now working on it`,
        "Failed to start sprint",
      ),
    [runSprintAction],
  );

  const handleComplete = useCallback(
    (sprint: Sprint) =>
      runSprintAction(
        sprint,
        () => sprintsApi.complete(sprint.id),
        `"${sprint.name}" marked as completed`,
        "Failed to complete sprint",
      ),
    [runSprintAction],
  );

  // Merge: active first, then queued
  const allQueueSprints = [
    ...(activeSprints ?? []),
    ...(queuedSprints ?? []),
  ];
  const queueCount = allQueueSprints.length;
  const planningCount = planningSprints?.length ?? 0;
  const isLoading =
    planningLoading || queuedLoading || activeLoading || historyLoading;
  const queueError = queuedError ?? activeError;

  // Position index within the queued (non-active) portion for ordinal labels
  const queuedOnlyIds = new Set((queuedSprints ?? []).map((s) => s.id));

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="h2">Sprints</Typography>
        <Typography variant="caption" color="text.secondary">
          All sprints — planning, queued, active, and completed
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Action error from a sprint transition (queue/un-queue/start/
            complete). Surfaced inline now that the password dialogs are
            gone — the JWT path can still fail on network or 5xx. */}
        {actionError && (
          <Alert
            severity="error"
            onClose={() => setActionError(null)}
            sx={{ mb: 1.5 }}
          >
            {actionError}
          </Alert>
        )}

        {/* ── Section 1: Planning ──────────────────────────── */}
        {!isLoading && (
          <>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 16,
                    borderRadius: "2px",
                    backgroundColor: "info.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{ color: "info.main", letterSpacing: "0.08em" }}
                >
                  Planning
                </Typography>
                <Chip
                  label={planningCount}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "0.65rem",
                    backgroundColor: "info.main",
                    color: "#fff",
                    borderRadius: "3px",
                  }}
                />
              </Box>

              {planningError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Failed to load planning sprints: {planningError}
                </Alert>
              )}

              {!planningError && planningCount === 0 && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No sprints in planning. Create one from the Kanban board.
                  </Typography>
                </Box>
              )}

              {(planningSprints ?? []).map((sprint) => (
                <PlanningSprintCard
                  key={sprint.id}
                  sprint={sprint}
                  onQueue={() => void handleQueueSprint(sprint)}
                  busy={busySprintId === sprint.id}
                  onOpenInBoard={
                    onOpenInBoard ? () => onOpenInBoard(sprint.id) : undefined
                  }
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ── Section 2: Sprint Queue ──────────────────────── */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 16,
                    borderRadius: "2px",
                    backgroundColor: "warning.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{ color: "warning.main", letterSpacing: "0.08em" }}
                >
                  Sprint Queue
                </Typography>
                <Chip
                  label={queueCount}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "0.65rem",
                    backgroundColor: "warning.main",
                    color: "#fff",
                    borderRadius: "3px",
                  }}
                />
              </Box>

              {queueError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Failed to load sprint queue: {queueError}
                </Alert>
              )}

              {!queueError && queueCount === 0 && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No sprints in the queue.
                  </Typography>
                </Box>
              )}

              {allQueueSprints.map((sprint, idx) => {
                const isActive = sprint.status === "active";
                // Position within the queued-only list for ordinal label
                const queuedIdx = queuedOnlyIds.has(sprint.id)
                  ? [...(queuedSprints ?? [])].findIndex((s) => s.id === sprint.id)
                  : -1;

                return (
                  <QueueSprintCard
                    key={sprint.id}
                    sprint={sprint}
                    isActive={isActive}
                    queuePosition={isActive ? null : queuedIdx + 1}
                    globalIndex={idx}
                    busy={busySprintId === sprint.id}
                    onUnqueue={isActive ? undefined : () => void handleUnqueue(sprint)}
                    onStart={isActive ? undefined : () => void handleStart(sprint)}
                    onComplete={isActive ? () => void handleComplete(sprint) : undefined}
                  />
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ── Section 3: Completed Sprints ─────────────── */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 16,
                    borderRadius: "2px",
                    backgroundColor: "success.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{ color: "success.main", letterSpacing: "0.08em" }}
                >
                  Completed Sprints
                </Typography>
                <Chip
                  label={history?.length ?? 0}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "0.65rem",
                    backgroundColor: "success.main",
                    color: "#fff",
                    borderRadius: "3px",
                  }}
                />
              </Box>

              {historyError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Failed to load sprint history: {historyError}
                </Alert>
              )}

              {!historyError && (!history || history.length === 0) && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No completed sprints yet.
                  </Typography>
                </Box>
              )}

              {history?.map((entry) => (
                <CompletedSprintCard key={entry.id} entry={entry} />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Greg's 2026-05-15 policy: admin JWT is sufficient for sprint
          queue transitions — the four password-dialog instances that
          used to live here (un-queue / start / complete / queue) were
          removed. Buttons now fire the action directly; busy state is
          per-sprint via `busySprintId`, and failures surface in the
          inline `actionError` Alert above. The `AdminPasswordDialog`
          component definition is retained for potential future use. */}

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

// ── PlanningSprintCard ────────────────────────────────────────

interface PlanningSprintCardProps {
  sprint: Sprint;
  onQueue: () => void;
  /** Disables the Queue button + shows a spinner while the API call
   *  for THIS sprint is in flight. Driven from the parent so the
   *  per-sprint busy state stays a single source of truth. */
  busy?: boolean;
  /** When omitted, the "Open in Board" button is hidden. */
  onOpenInBoard?: () => void;
}

function PlanningSprintCard({
  sprint,
  onQueue,
  busy = false,
  onOpenInBoard,
}: PlanningSprintCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [loadedDetail, setLoadedDetail] = useState<SprintWithItems | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loadedDetail) {
      setLoadingDetail(true);
      try {
        const detail = await sprintsApi.get(sprint.id);
        setLoadedDetail(detail);
      } catch {
        setLoadedDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const createdDate = sprint.created_at
    ? new Date(sprint.created_at).toLocaleDateString()
    : null;

  return (
    <Paper sx={{ mb: 1.5, overflow: "hidden" }}>
      {/* Sprint header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        onClick={() => void handleExpand()}
      >
        <EditNoteOutlinedIcon
          sx={{ color: "info.main", fontSize: "1rem", mr: 1.5, flexShrink: 0 }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" noWrap>
              {sprint.name}
            </Typography>
            <Chip
              label="Planning"
              size="small"
              sx={{
                borderRadius: "3px",
                backgroundColor: "info.main",
                color: "#fff",
                fontSize: "0.7rem",
                height: "20px",
              }}
            />
          </Box>

          {sprint.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {sprint.description}
            </Typography>
          )}

          {createdDate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Created {createdDate}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1, flexShrink: 0 }}>
          <Tooltip title="Add this sprint to the queue — Paul will work it in FIFO order">
            <span>
              {/* Tooltip needs a non-disabled wrapper so the hover still
                  registers while the button is disabled mid-request. */}
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                startIcon={
                  busy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon fontSize="small" />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onQueue();
                }}
                sx={{
                  backgroundColor: "success.main",
                  color: "#fff",
                  "&:hover": { backgroundColor: "success.dark" },
                }}
              >
                Queue Sprint
              </Button>
            </span>
          </Tooltip>

          {onOpenInBoard && (
            <Tooltip title="Open this sprint in the Kanban board to edit cards">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ViewKanbanOutlinedIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenInBoard();
                }}
              >
                Open in Board
              </Button>
            </Tooltip>
          )}

          <IconButton
            size="small"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              void handleExpand();
            }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {loadingDetail && <CircularProgress size={20} />}

          {!loadingDetail && loadedDetail && loadedDetail.items.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No cards in this sprint yet.
            </Typography>
          )}

          {!loadingDetail && loadedDetail && loadedDetail.items.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {loadedDetail.items.map((item, idx) => (
                <Box key={item.id}>
                  {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 80, flexShrink: 0 }}
                    >
                      {item.kanban_column}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {item.card.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TagList tags={item.card.tags} max={2} />
                      <PriorityBadge priority={item.card.priority ?? "Medium"} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── QueueSprintCard ───────────────────────────────────────────

interface QueueSprintCardProps {
  sprint: Sprint;
  isActive: boolean;
  queuePosition: number | null;
  globalIndex: number;
  /** Disables transition buttons + shows a spinner while the API call
   *  for THIS sprint is in flight. */
  busy?: boolean;
  onUnqueue?: () => void;
  onStart?: () => void;
  onComplete?: () => void;
}

function QueueSprintCard({
  sprint,
  isActive,
  queuePosition,
  busy = false,
  onUnqueue,
  onStart,
  onComplete,
}: QueueSprintCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [loadedDetail, setLoadedDetail] = useState<SprintWithItems | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loadedDetail) {
      setLoadingDetail(true);
      try {
        const detail = await sprintsApi.get(sprint.id);
        setLoadedDetail(detail);
      } catch {
        setLoadedDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const confirmedDate = sprint.confirmed_at
    ? new Date(sprint.confirmed_at).toLocaleDateString()
    : null;

  // Active sprints get a green left border accent; queued get the standard warning
  const accentColor = isActive ? "success.main" : "warning.main";

  return (
    <Paper
      sx={[
        { mb: 1.5, overflow: "hidden" },
        isActive && {
          borderLeft: "3px solid",
          borderColor: "success.main",
        },
      ]}
    >
      {/* Sprint header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        onClick={() => void handleExpand()}
      >
        {isActive ? (
          <AutorenewIcon
            sx={{ color: "success.main", fontSize: "1rem", mr: 1.5, flexShrink: 0 }}
          />
        ) : (
          <QueueOutlinedIcon
            sx={{ color: "warning.main", fontSize: "1rem", mr: 1.5, flexShrink: 0 }}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" noWrap>
              {sprint.name}
            </Typography>

            {isActive ? (
              <Chip
                label="Active — In Progress"
                size="small"
                sx={{
                  borderRadius: "3px",
                  backgroundColor: "success.main",
                  color: "#fff",
                  fontSize: "0.7rem",
                  height: "20px",
                }}
              />
            ) : (
              queuePosition !== null && (
                <Chip
                  label={`${ordinal(queuePosition)} in queue`}
                  size="small"
                  sx={{
                    borderRadius: "3px",
                    backgroundColor: "warning.main",
                    color: "#fff",
                    fontSize: "0.7rem",
                    height: "20px",
                  }}
                />
              )
            )}
          </Box>

          {sprint.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {sprint.description}
            </Typography>
          )}

          {confirmedDate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {isActive ? "Started" : "Queued"} {confirmedDate}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1, flexShrink: 0 }}>
          {/* Active: Complete Sprint button */}
          {isActive && onComplete && (
            <Tooltip title="Mark this sprint as completed">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={busy}
                  startIcon={
                    busy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckCircleOutlineIcon fontSize="small" />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete();
                  }}
                  sx={{
                    backgroundColor: "success.main",
                    color: "#fff",
                    "&:hover": { backgroundColor: "success.dark" },
                  }}
                >
                  Complete Sprint
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Queued: Start Sprint + Un-queue buttons */}
          {!isActive && (
            <>
              {onStart && (
                <Tooltip title="Mark this sprint as active — Paul will start working on it">
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={busy}
                      startIcon={
                        busy ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PlayArrowOutlinedIcon fontSize="small" />
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onStart();
                      }}
                      sx={{
                        backgroundColor: "success.main",
                        color: "#fff",
                        "&:hover": { backgroundColor: "success.dark" },
                      }}
                    >
                      Start Sprint
                    </Button>
                  </span>
                </Tooltip>
              )}

              {onUnqueue && (
                <Tooltip title="Move sprint back to planning">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      disabled={busy}
                      startIcon={
                        busy ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <RemoveCircleOutlineIcon fontSize="small" />
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnqueue();
                      }}
                    >
                      Un-queue
                    </Button>
                  </span>
                </Tooltip>
              )}
            </>
          )}

          <IconButton
            size="small"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              void handleExpand();
            }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
            <LockOutlinedIcon sx={{ fontSize: "0.8rem", color: accentColor }} />
            <Typography variant="caption" sx={{ color: accentColor }}>
              {isActive
                ? "Sprint is active — Paul is working on it"
                : "Read-only — sprint is queued for Paul"}
            </Typography>
          </Box>

          {loadingDetail && <CircularProgress size={20} />}

          {!loadingDetail && loadedDetail && loadedDetail.items.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No cards in this sprint yet.
            </Typography>
          )}

          {!loadingDetail && loadedDetail && loadedDetail.items.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {loadedDetail.items.map((item, idx) => (
                <Box key={item.id}>
                  {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 80, flexShrink: 0 }}
                    >
                      {item.kanban_column}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {item.card.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TagList tags={item.card.tags} max={2} />
                      <PriorityBadge priority={item.card.priority ?? "Medium"} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── CompletedSprintCard ───────────────────────────────────────

interface CompletedSprintCardProps {
  entry: SprintHistoryEntry;
}

function CompletedSprintCard({ entry }: CompletedSprintCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [loadedCards, setLoadedCards] = useState<FeatureCard[] | null>(null);
  const [loadingCards, setLoadingCards] = useState(false);

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loadedCards) {
      setLoadingCards(true);
      try {
        const detail = await sprintHistoryApi.get(entry.sprint_id);
        setLoadedCards(detail.completed_cards ?? []);
      } catch {
        setLoadedCards([]);
      } finally {
        setLoadingCards(false);
      }
    }
  };

  return (
    <Paper sx={{ mb: 1.5, overflow: "hidden" }}>
      {/* Sprint header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        onClick={() => void handleExpand()}
      >
        <CheckCircleOutlineIcon
          sx={{ color: "success.main", fontSize: "1rem", mr: 1.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {entry.sprint_name ?? "Sprint"}
            {entry.sprint_description && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                — {entry.sprint_description}
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Completed {new Date(entry.completed_at).toLocaleDateString()}
          </Typography>
        </Box>

        {/* Stats */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1, flexShrink: 0 }}>
          <Tooltip title="Cards completed">
            <Chip
              label={`${entry.cards_completed} done`}
              size="small"
              sx={{
                borderRadius: "3px",
                backgroundColor: "success.main",
                color: "#fff",
                fontSize: "0.7rem",
              }}
            />
          </Tooltip>
          {entry.cards_carried_over > 0 && (
            <Tooltip title="Cards carried over to next sprint">
              <Chip
                label={`${entry.cards_carried_over} carried`}
                size="small"
                color="warning"
                sx={{ borderRadius: "3px", fontSize: "0.7rem" }}
              />
            </Tooltip>
          )}
          {entry.total_effort && (
            <Chip
              label={entry.total_effort}
              size="small"
              variant="outlined"
              sx={{ borderRadius: "3px", fontSize: "0.7rem" }}
            />
          )}
        </Stack>

        <IconButton size="small" tabIndex={-1}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {entry.summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {entry.summary}
            </Typography>
          )}

          {loadingCards && <CircularProgress size={20} />}

          {loadedCards && loadedCards.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No card details available.
            </Typography>
          )}

          {loadedCards && loadedCards.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {loadedCards.map((card, idx) => (
                <Box key={card.id}>
                  {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: "success.main", fontSize: "0.875rem", flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {card.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TagList tags={card.tags} max={2} />
                      <PriorityBadge priority={card.priority ?? "Medium"} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
